import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FieldArray, Form, Formik } from "formik";
import React, { useState } from "react";
import useAxiosInstance from "../../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../../enums/common.enum";
import { errorToast, handleError } from "../../../utils/common";
import CustomDialogTitle from "../../../components/Common/customDialogTitle";
import AddressDetail from "../SingleBorrower/Summary/components/AddressDetail";
import { updateAddressContactDetailsSchema } from "../../../schemas/providers.schema";

const EditContactDetailsModal = ({
  showEditContactDetailsModal,
  setShowEditContactDetailsModal,
  borrowerInfo,
  setBorrowerInfo,
}: any) => {
  const [loading, setLoading] = useState(false);
  const privateInstance = useAxiosInstance();

  const handleSubmit = async (values: any, actions: any) => {
    let payload: any = {
      borrower_id: borrowerInfo?.borrower_id,
      contact_details: {
        address: values?.address,
        pincode: values?.pin,
        city: values?.city,
        state: values?.state,
        contact_no1: values?.contact_no1,
        contact_no2: values?.contact_no2,
        email_ids: values?.email_ids,
      },
    };

    setLoading(true);

    try {
      const result = await privateInstance.post(
        `${API_VERSION.V3}/borrowers`,
        payload,
      );
      if (result?.data?.status === API_RESULT.SUCCESS) {
        setShowEditContactDetailsModal(false);
        const resultResponse = result.data.response;
        //update provider info in modal
        setBorrowerInfo((prev: any) => ({
          ...prev,
          contact_details: {
            ...prev.contact_details,
            ...resultResponse?.contact_details,
          },
        }));
        //end
      } else {
        errorToast(result.data.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={showEditContactDetailsModal}
      onClose={() => setShowEditContactDetailsModal(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="md"
    >
      <CustomDialogTitle
        id="customized-dialog-title"
        onClose={() => setShowEditContactDetailsModal(false)}
      >
        Edit Contact Details
      </CustomDialogTitle>
      <DialogContent sx={{ overflowY: "scroll" }}>
        <Formik
          initialValues={{
            address: borrowerInfo?.contact_details?.address || "",
            pin: borrowerInfo?.contact_details?.pincode || "",
            city: borrowerInfo?.contact_details?.city || "",
            state: borrowerInfo?.contact_details?.state || "",
            contact_no1: borrowerInfo?.contact_details?.contact_no1 || "",
            contact_no2: borrowerInfo?.contact_details?.contact_no2 || "",
            email_ids: borrowerInfo?.contact_details?.email_ids || [""],
          }}
          validationSchema={updateAddressContactDetailsSchema}
          onSubmit={(values: any, actions: any) => {
            actions.setSubmitting(false);
            handleSubmit(values, actions);
          }}
        >
          {(props) => {
            const {
              values,
              setFieldValue,
              handleBlur,
              errors,
              touched,
              setTouched,
            } = props;
            return (
              <Form>
                <AddressDetail
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  formData={values}
                  setTouched={setTouched}
                  serviceabilityCheck={false}
                  showLandmarkField={false}
                />
                <Stack direction="row" justifyContent="space-between">
                  <TextField
                    fullWidth
                    id="contact_no1"
                    label="Contact Number (1)"
                    size="small"
                    value={values.contact_no1 || ""}
                    sx={{ mt: 1.5, mr: { xs: 0, md: 1 } }}
                    onChange={(e) =>
                      setFieldValue("contact_no1", e.target.value)
                    }
                    onBlur={handleBlur}
                    helperText={
                      errors.contact_no1 && touched.contact_no1
                        ? errors.contact_no1
                        : ""
                    }
                    error={
                      errors.contact_no1 && touched.contact_no1 ? true : false
                    }
                    InputLabelProps={{
                      required: true,
                    }}
                  />
                  <TextField
                    fullWidth
                    id="contact_no2"
                    label="Contact Number (2)"
                    size="small"
                    value={values.contact_no2 || ""}
                    sx={{ mt: 1.5 }}
                    onChange={(e) =>
                      setFieldValue("contact_no2", e.target.value)
                    }
                    onBlur={handleBlur}
                    helperText={
                      errors.contact_no2 && touched.contact_no2
                        ? errors.contact_no2
                        : ""
                    }
                    error={
                      errors.contact_no2 && touched.contact_no2 ? true : false
                    }
                    InputLabelProps={{
                      required: true,
                    }}
                  />
                </Stack>
                <Box
                  mt={2}
                  p={2}
                  border="1px solid"
                  borderColor="divider"
                  borderRadius={2}
                  bgcolor="background.paper"
                >
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Email IDs
                  </Typography>

                  <FieldArray name="email_ids">
                    {({ push, remove }) => {
                      const emailErrors = Array.isArray(errors.email_ids)
                        ? errors.email_ids
                        : [];
                      const emailTouched = Array.isArray(touched.email_ids)
                        ? touched.email_ids
                        : [];

                      return (
                        <Stack spacing={1.5}>
                          {values.email_ids.map(
                            (email: string, index: number) => (
                              <Stack
                                key={index}
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <TextField
                                  fullWidth
                                  label={`Email ${index + 1}`}
                                  size="small"
                                  value={email}
                                  onChange={(e) =>
                                    setFieldValue(
                                      `email_ids.${index}`,
                                      e.target.value.toLowerCase(),
                                    )
                                  }
                                  onBlur={handleBlur}
                                  error={Boolean(
                                    emailTouched[index] && emailErrors[index],
                                  )}
                                  helperText={
                                    emailTouched[index] && emailErrors[index]
                                  }
                                />

                                {values.email_ids.length > 1 && (
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() => remove(index)}
                                  >
                                    ✕
                                  </IconButton>
                                )}
                              </Stack>
                            ),
                          )}

                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => push("")}
                            sx={{ alignSelf: "flex-start" }}
                          >
                            + Add Email
                          </Button>
                        </Stack>
                      );
                    }}
                  </FieldArray>
                </Box>

                <Box textAlign="center" mt={3}>
                  <LoadingButton
                    size="medium"
                    color="primary"
                    loading={loading}
                    loadingIndicator={
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    }
                    variant="contained"
                    type="submit"
                  >
                    Update
                  </LoadingButton>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default EditContactDetailsModal;
